/**
 * Fathead Customizer Integration Module
 *
 * This module handles the integration between the Shopify store and the customizer application.
 * It manages the modal overlay, product variant selection, and cart interactions.
 */
(function() {
  //#region Configuration and Constants

  // Basic configuration
  const config = {
    customizerUrl: window.FATHEAD_CONFIG?.customizerUrl || 'https://fathead-customizer.vercel.app',
    buttonId: 'fathead-customizer-btn'
  };

  // Allowed origins for postMessage communication
  const defaultOrigins = [
    'https://fathead-customizer.vercel.app',
    'https://www.fathead-customizer.vercel.app',
    'https://fathead.com',
    'https://www.fathead.com',
    'null'
  ];

  const allowedOrigins = [
    ...(window.FATHEAD_CONFIG?.allowedOrigins || []),
    ...defaultOrigins
  ];


  //#endregion

  //#region Initialization

  /**
   * Initializes the customizer modal functionality
   * @param {Object} options - Optional configuration overrides
   */
  function init(options = {}) {
    Object.assign(config, options);
    setupCustomizerButton();
  }

  /**
   * Sets up the customizer button click handler
   */
  function setupCustomizerButton() {
    const customizerBtn = document.getElementById(config.buttonId);
    if (!customizerBtn) {
      console.warn('[Customizer] Button not found:', config.buttonId);
      return;
    }

    console.log('[Customizer] Button found and ready');


    customizerBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const productId = customizerBtn.getAttribute('data-product-id');
      const variantId = getVariantId(customizerBtn);

      const overlay = createCustomizerOverlay(productId, variantId);
      document.body.appendChild(overlay);
      document.body.style.overflow = 'hidden';

      setupOverlayListeners(overlay);
    });
  }

  //#endregion

  //#region UI Creation and Management

  /**
   * Creates the customizer overlay with iframe
   * @param {string} productId - The product ID
   * @param {string} variantId - The variant ID
   * @returns {HTMLElement} The overlay element
   */
  function createCustomizerOverlay(productId, variantId) {
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;background:rgba(0,0,0,0.5);z-index:9999999;display:flex;align-items:center;justify-content:center;padding: 20px 20px 40px 20px;';

    // Check if wooden handle checkbox exists and get its value
    let woodenHandleValue = null;
    const woodenHandleCheckbox = document.querySelector('input[name="properties[Include A Wooden Handle (Free - U.S. Residents Only)]"][type="checkbox"]');
    if (woodenHandleCheckbox) {
      woodenHandleValue = woodenHandleCheckbox.checked ? 'Yes' : 'No';
    }

    // Build the URL with all parameters
    let iframeUrl = `${config.customizerUrl}?product_id=${productId}&variant_id=${variantId}&embedded=true`;

    // Add wooden handle parameter if it exists
    if (woodenHandleValue !== null) {
      iframeUrl += `&wooden_handle=${woodenHandleValue}`;
    }

    overlay.innerHTML = `<iframe
      src="${iframeUrl}"
      style="width:90%;height:90%;max-width:1500px;max-height:800px;border:none;border-radius:8px;background:white;"
      frameborder="0"
      allowtransparency="true"
      allowfullscreen="true"
      scrolling="yes"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture">
    </iframe>`;

    return overlay;
  }

  /**
   * Closes the customizer overlay and restores page scrolling
   * @param {HTMLElement} overlay - The overlay DOM element to remove
   */
  function closeCustomizerOverlay(overlay) {
    if (overlay) {
      // Remove the message handler to prevent memory leaks and duplicate handlers
      if (overlay._messageHandler) {
        window.removeEventListener('message', overlay._messageHandler);
      }

      overlay.remove();

      // Force restore scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.documentElement.style.overflow = '';

      console.log('[Customizer] Overlay closed, scroll restored');
    }
  }

  //#endregion

  //#region Event Handling

  /**
   * Sets up event listeners for the customizer overlay
   * @param {HTMLElement} overlay - The overlay element
   */
  function setupOverlayListeners(overlay) {
    // Handle clicking outside the iframe
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeCustomizerOverlay(overlay);
      }
    });

    // Create named handler so we can remove it
    const messageHandler = function(event) {
      if (!isAllowedOrigin(event.origin)) {
        console.log('Ignoring message from unauthorized origin:', event.origin);
        return;
      }

      handleCustomizerMessage(event.data, overlay);
    };

    // Add message listener
    window.addEventListener('message', messageHandler);

    // Store the handler on the overlay so we can clean it up
    overlay._messageHandler = messageHandler;
  }

  /**
   * Handles messages from the customizer iframe
   * @param {Object} data - The message data
   * @param {HTMLElement} overlay - The overlay element
   */
  function handleCustomizerMessage(data, overlay) {
    switch (data.type) {
      case 'CART_UPDATED':
        closeCustomizerOverlay(overlay);
        if (data.success && window.theme && window.theme.cartDrawer) {
          window.theme.cartDrawer.refresh({ open: true });
        }
        break;

      case 'CLOSE_CUSTOMIZER':
        closeCustomizerOverlay(overlay);
        break;

      case 'ADD_TO_CART':
        handleAddToCart(data.items, overlay);
        break;
    }
  }

  //#endregion

  //#region Cart Operations

  /**
   * Handles adding items to cart
   * @param {Array} items - The items to add to cart
   * @param {HTMLElement} overlay - The overlay element
   */
  function handleAddToCart(items, overlay) {
    if (overlay.dataset.cartAdding === 'true') return;
    overlay.dataset.cartAdding = 'true';
    window.FatheadCart.add({ items: items }, { source: 'customizer_iframe', open: false })
      .then(function () {
        closeCustomizerOverlay(overlay);
        window.FatheadCart.open();
      }, function (error) {
        overlay.dataset.cartAdding = 'false';
        // Leave the customizer open; never retry a mutation after an unknown outcome.
        var status = overlay.querySelector('[data-cart-error]');
        if (!status) {
          status = document.createElement('p');
          status.setAttribute('data-cart-error', '');
          status.setAttribute('role', 'alert');
          overlay.appendChild(status);
        }
        status.textContent = error.description || error.message;
      });
  }

  //#endregion

  //#region Utility Functions

  /**
   * Gets the variant ID from URL params or button attribute
   * @param {HTMLElement} button - The customizer button element
   * @returns {string} The variant ID
   */
  function getVariantId(button) {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('variant')) {
      const variantId = urlParams.get('variant');
      console.log('Using variant ID from URL:', variantId);
      return variantId;
    } else {
      const variantId = button.getAttribute('data-variant-id');
      console.log('Using variant ID from button attribute:', variantId);
      return variantId;
    }
  }

  /**
   * Checks if the origin is allowed for postMessage communication
   * @param {string} origin - The origin to check
   * @returns {boolean} Whether the origin is allowed
   */
  function isAllowedOrigin(origin) {
    console.log('[Customizer] Checking origin:', origin);

    // Allow null origin in development environments
    if (origin === 'null') {
      return true;
    }

    try {
      // Parse as URL to properly extract just the origin
      const url = new URL(origin);
      const originOnly = url.origin;

      // Use exact matching
      const isAllowed = allowedOrigins.includes(originOnly);
      console.log('[Customizer] Origin check result:', isAllowed);
      return isAllowed;
    } catch (e) {
      console.warn('[Customizer] Invalid origin format:', origin);
      return false;
    }
  }

  //#endregion

  // Expose the init function globally
  window.CustomizerModal = { init };
})();
